import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import MobileLoginView from '../components/auth/MobileLoginView';
import MobileRoleSelection from '../components/auth/MobileRoleSelection';
import MobileAthleteForm from '../components/auth/MobileAthleteForm';
import MobileParentForm from '../components/auth/MobileParentForm';
import MobileCoachForm from '../components/auth/MobileCoachForm';

type AuthView = 'login' | 'role-selection' | 'registration';
type UserRole = 'athlete' | 'parent' | 'club_coach' | 'college_coach';

interface MobileAuthScreenProps {
  onLogin: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  onBack?: () => void;
  showBackButton?: boolean;
  onRegistrationSuccess?: () => void;
}

const MobileAuthScreen: React.FC<MobileAuthScreenProps> = ({
  onLogin,
  onBack,
  showBackButton = false,
  onRegistrationSuccess
}) => {
  const [view, setView] = useState<AuthView>('login');
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);

  const handleRoleSelect = (role: UserRole) => {
    setSelectedRole(role);
    setView('registration');
  };

  const handleBackToLogin = () => {
    setView('login');
    setSelectedRole(null);
  };

  const handleBackToRoleSelection = () => {
    setView('role-selection');
    setSelectedRole(null);
  };

  const renderRegistrationForm = () => {
    switch (selectedRole) {
      case 'athlete':
        return (
          <MobileAthleteForm 
            onBack={handleBackToRoleSelection}
            onSuccess={onRegistrationSuccess}
          />
        );
      case 'parent':
        return (
          <MobileParentForm 
            onBack={handleBackToRoleSelection}
            onSuccess={onRegistrationSuccess}
          />
        );
      case 'club_coach':
        return (
          <MobileCoachForm 
            coachType="club_coach"
            onBack={handleBackToRoleSelection}
            onSuccess={onRegistrationSuccess}
          />
        );
      case 'college_coach':
        return (
          <MobileCoachForm 
            coachType="college_coach"
            onBack={handleBackToRoleSelection}
            onSuccess={onRegistrationSuccess}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Back button for main screen */}
      {showBackButton && onBack && view === 'login' && (
        <div className="absolute top-4 left-4 z-10">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back</span>
          </button>
        </div>
      )}

      {view === 'login' && (
        <MobileLoginView
          onLogin={onLogin}
          onGetStarted={() => setView('role-selection')}
        />
      )}

      {view === 'role-selection' && (
        <MobileRoleSelection
          onBack={handleBackToLogin}
          onSelectRole={handleRoleSelect}
        />
      )}

      {view === 'registration' && selectedRole && renderRegistrationForm()}
    </div>
  );
};

export default MobileAuthScreen;
